import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { PHOTOS } from '@/data/site';
import { Photo } from './fx/Photo';
import { Reveal } from './fx/Reveal';
import { burst } from './fx/Burst';
import { Heart, Sparkle, Paw, Squiggle } from './brand/Shapes';

// Texto original do site, com fotos reais "costuradas" entre as palavras.
const TOKENS = [
  'Na', 'Cãopanhia', 'Pet,', 'cada', 'pet', { photo: PHOTOS.siames },
  'é', 'tratado', 'com', { word: 'amor', em: 'text-pet-pink-deep' }, { photo: PHOTOS.yorkshireFofo }, 'e', 'dedicação.',
  'Nosso', 'objetivo', 'é', 'proporcionar', { word: 'bem-estar', em: 'text-pet-blue' }, { photo: PHOTOS.posBanho },
  'e', { word: 'felicidade', em: 'text-pet-purple' }, 'para', 'seu', 'melhor', 'amigo!', { photo: PHOTOS.oculosEmPe, pos: '50% 25%' },
];

const Word = ({ progress, range, children, em }) => {
  const opacity = useTransform(progress, range, [0.18, 1]);
  const y = useTransform(progress, range, [8, 0]);
  return (
    <motion.span style={{ opacity, y }} className={`inline-block mr-[0.24em] ${em || ''}`}>
      {children}
    </motion.span>
  );
};

const InlinePhoto = ({ progress, range, photo, pos }) => {
  const scale = useTransform(progress, range, [0, 1]);
  const rotate = useTransform(progress, range, [-30, -4]);
  return (
    <motion.span
      style={{ scale, rotate }}
      className="inline-block align-[-0.12em] h-[0.9em] w-[1.5em] mr-[0.24em] rounded-full overflow-hidden ring-[3px] ring-white shadow-lg"
    >
      <Photo photo={photo} sizes="160px" alt="" className="w-full h-full object-cover" style={{ objectPosition: pos || '50% 40%' }} />
    </motion.span>
  );
};

// Os três valores do site original, como adesivos coloridos
const VALUES = [
  { word: 'Carinho', Icon: Heart, bg: 'bg-pet-pink', ink: 'text-pet-pink-ink', icon: 'text-pet-pink-deep', rot: -4 },
  { word: 'Qualidade', Icon: Sparkle, bg: 'bg-pet-sky', ink: 'text-pet-blue-deep', icon: 'text-pet-blue', rot: 3 },
  { word: 'Confiança', Icon: Paw, bg: 'bg-pet-mint', ink: 'text-pet-mint-ink', icon: 'text-pet-mint-deep', rot: -2 },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.85', 'end 0.55'] });
  const total = TOKENS.length;

  return (
    <section data-nav="inicio" aria-label="Sobre a Cãopanhia Pet" className="relative bg-pet-cream pt-10 pb-24 md:pt-16 md:pb-36">
      <div className="container-cp">
        <p className="kicker kicker-ears bg-pet-pink-soft text-pet-pink-ink mb-8 md:mb-12 -rotate-2">
          <Heart className="w-4 h-4 text-pet-pink-deep" /> Sobre a Cãopanhia
        </p>

        <p
          ref={ref}
          className="relative font-display font-medium text-pet-ink text-[clamp(1.8rem,5vw,4.3rem)] leading-[1.15] max-w-[1200px]"
        >
          {TOKENS.map((token, i) => {
            const start = i / total;
            const range = reduce ? [0, 0.0001] : [start, Math.min(1, start + 1.6 / total)];
            if (typeof token === 'string') {
              return (
                <Word key={i} progress={scrollYProgress} range={range}>
                  {token}
                </Word>
              );
            }
            if (token.photo) {
              return <InlinePhoto key={i} progress={scrollYProgress} range={range} photo={token.photo} pos={token.pos} />;
            }
            return (
              <Word key={i} progress={scrollYProgress} range={range} em={token.em}>
                {token.word}
              </Word>
            );
          })}
        </p>
        <Squiggle className="mt-6 w-40 md:w-56 text-pet-pink" />

        <ul className="mt-14 md:mt-20 grid sm:grid-cols-3 gap-4 md:gap-6">
          {VALUES.map((v, i) => (
            <Reveal as="li" key={v.word} delay={i * 0.1} rotate={v.rot * 3}>
              <motion.button
                type="button"
                onClick={(e) => burst(e, i === 0 ? 'heart' : i === 1 ? 'star' : 'paw')}
                className={`w-full ${v.bg} ${v.ink} rounded-[32px] p-6 md:p-8 flex items-center gap-4 shadow-[0_18px_40px_-24px_rgba(45,55,72,0.5)] text-left`}
                style={{ rotate: v.rot }}
                whileHover={{ rotate: 0, scale: 1.04, y: -6 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 12 }}
              >
                <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                  <v.Icon className={`w-7 h-7 md:w-8 md:h-8 ${v.icon}`} />
                </span>
                <span className="font-display text-[2rem] md:text-[2.6rem] leading-none">{v.word}</span>
              </motion.button>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Manifesto;
